import { AppBar, Button, IconButton, Stack, Toolbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useAuthContext } from '../../../auth/useAuthContext';
import { GAEventPopover, useAnalyticsContext } from '../../../analytics';
import Iconify from '../../../components/iconify';
import LoadingScreen from '../../../components/loading-screen';
import Logo from '../../../components/logo';
import { useSettingsContext } from '../../../components/settings';
import { HEADER, NAV } from '../../../config-global';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
import PortalNotificationBanner from '../../../portalNotification/components/PortalNotificationBanner';
import { bgBlur } from '../../../utils/cssStyles';
import { hasSessionAdmin } from '../../../utils/mrr/emulator/signInAsOtherUser';
import { roleCanSeePortalNotifications } from '../../../utils/mrr/userConstants';
import AccountPopover from './AccountPopover';
import { ContactUsPopover } from './ContactUsPopover';
import PortalNotificationsPopover from './NotificationsPopover';
import ReturnToAdminPopover from './ReturnToAdminPopover';
// import LanguagePopover from './LanguagePopover';
// import ContactsPopover from './ContactsPopover';

// ----------------------------------------------------------------------

type Props = {
  showNotifications?: boolean;
  onOpenNav?: VoidFunction;
};

export default function Header({ onOpenNav, showNotifications = true }: Props) {
  const theme = useTheme();
  const { user } = useAuthContext();
  const { sendEvent } = useAnalyticsContext();
  const canSeePN = user ? roleCanSeePortalNotifications(user.role) : true;

  const { themeLayout } = useSettingsContext();

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');
  const isMobile = useResponsive('down', 'sm');
  const isSlim = useResponsive('down', 350);
  const hasAdmin = hasSessionAdmin();
  const isMobileWithAdmin = isMobile && hasAdmin;

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP);

  const [loggingOutOfOtherUser] = useState(false);
  const [openContactUsPopover, setOpenContactUsPopover] = useState<HTMLElement | null>(null);

  const handleOpenContactUs = (event: React.MouseEvent<HTMLElement>) => {
    setOpenContactUsPopover(event.currentTarget);
    sendEvent(new GAEventPopover('Support'));
  };
  const handleCloseContactUs = () => setOpenContactUsPopover(null);

  const renderContent = (
    <Stack flexGrow={1} direction="row" alignItems="center" justifyContent="space-between">
      {!isDesktop && (
        <IconButton
          onClick={onOpenNav}
          aria-label="Open navigation"
          sx={{ mr: 1, color: 'text.primary' }}
        >
          <Iconify icon="eva:menu-2-fill" />
        </IconButton>
      )}

      {/* Only lets us browse (all) routes. Does more harm than good in this app.
          The styling is nice, if this could come back for a more useful search feature.
      */}
      {/* <Searchbar /> */}

      {/* Show our logo when the permanently-open drawer is hidden. */}
      {!isDesktop && (
        <Logo width={isMobileWithAdmin || canSeePN ? 180 : undefined} disabledLink full={!isSlim} />
      )}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 1, sm: 1.5 }}
        // spacing={{ xs: 0.5, sm: 1.5 }}
      >
        {/* <LanguagePopover /> */}

        {hasAdmin && <ReturnToAdminPopover />}

        <Button
          variant="text"
          sx={{ color: openContactUsPopover ? 'primary.main' : 'text.secondary' }}
          onClick={handleOpenContactUs}
        >
          Contact Us
        </Button>
        <ContactUsPopover open={openContactUsPopover} onClose={handleCloseContactUs} />

        {canSeePN && <PortalNotificationsPopover />}

        {/* <ContactsPopover /> */}

        {/* <CartPopover /> */}
        <AccountPopover />
      </Stack>
    </Stack>
  );

  if (loggingOutOfOtherUser) {
    return <LoadingScreen />;
  }

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar + 1,
        display: 'flex',
        flexDirection: 'column',
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: 'unset',
          height: HEADER.H_MOBILE,
          px: { xs: 1, lg: 5 },
          ...(isDesktop && {
            height: HEADER.H_DASHBOARD_DESKTOP,
            ...(isOffset && { height: HEADER.H_DASHBOARD_DESKTOP_OFFSET }),
          }),
        }}
      >
        {renderContent}
      </Toolbar>
      {showNotifications && canSeePN && <PortalNotificationBanner />}
    </AppBar>
  );
}
